import {
  getCanonicalConditionValue,
  getConditionLabel,
} from "../generated/productConditions";
import { stripProductSourceLocation } from "./product-source-location";

export const MAX_GENERATED_TITLE_LENGTH = 32;

export interface ProductDisplayNameInput {
  condition?: string | null;
  productName?: string | null;
  productType?: string | null;
  productTypeSlug?: string | null;
}

const KNOWN_ACRONYMS = new Set([
  "hdpe",
  "ldpe",
  "pp",
  "pet",
  "pvc",
  "abs",
  "eps",
  "ibc",
  "fibc",
]);

export function humanizeProductValue(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (KNOWN_ACRONYMS.has(trimmed.toLowerCase())) return trimmed.toUpperCase();
  if (trimmed.length <= 2) return trimmed.toUpperCase();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function formatProductTypeLabel(productType: string): string {
  return productType
    .trim()
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(humanizeProductValue)
    .join(" ");
}

export function formatConditionLabel(
  condition: string | undefined | null,
  productTypeSlug?: string | null,
): string | null {
  if (!condition) return null;
  const canonicalValue = productTypeSlug
    ? getCanonicalConditionValue(productTypeSlug, condition)
    : null;
  const canonicalLabel =
    canonicalValue && productTypeSlug
      ? getConditionLabel(productTypeSlug, canonicalValue)
      : null;
  if (canonicalLabel) {
    return canonicalLabel
      .replace(/\s*\([^)]*\)\s*$/, (match) => (/\d/.test(match) ? match : ""))
      .trim();
  }
  return condition
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(humanizeProductValue)
    .join(" ");
}

export function formatProductDisplayName({
  condition,
  productName,
  productType,
  productTypeSlug,
}: ProductDisplayNameInput): string {
  const typeSource = productType?.trim() || productTypeSlug?.trim();
  if (!typeSource) return stripProductSourceLocation(productName ?? "");
  const typeLabel = formatProductTypeLabel(typeSource);
  const conditionLabel = formatConditionLabel(condition, productTypeSlug);
  return conditionLabel ? `${conditionLabel} ${typeLabel}` : typeLabel;
}

export function formatLineDisplayName({
  condition,
  productName,
  productTypeSlug,
  productNameOverridden,
}: Omit<ProductDisplayNameInput, "productType"> & {
  productNameOverridden?: boolean | null;
}): string {
  if (productNameOverridden) {
    return stripProductSourceLocation(productName ?? "");
  }
  return formatProductDisplayName({ condition, productName, productTypeSlug });
}

export function isChosenListingTitle(listing: {
  titleSource?: string | null;
}): boolean {
  return listing.titleSource === "manual";
}

export function formatListingLineName(listing: {
  title: string;
  titleSource?: string | null;
  condition?: string | null;
  productTypeSlug?: string | null;
}): string {
  return formatLineDisplayName({
    condition: listing.condition,
    productName: listing.title,
    productTypeSlug: listing.productTypeSlug,
    productNameOverridden: isChosenListingTitle(listing),
  });
}
